import { useCallback } from 'react';

import { CONFIRM_POPUP_MODE, useConfirmPopup } from 'src/components/ConfirmPopup';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { getLanguageWithoutFormality, isSameLanguage } from 'src/data/generation';
import { getToolChannel } from 'src/data/generationTool';
import * as events from 'src/lib/events';
import * as log from 'src/lib/log';
import { formatBrandVoiceChildRoute, formatRoute } from 'src/lib/routing';
import { ROUTE_PARAMS, ROUTES } from 'src/routes';
import { useCreateCustomModel } from 'src/services/useCreateCustomModel';
import { useErrorHandler } from 'src/services/useErrorHandler';
import { useIsApplicationModeInfinity } from 'src/services/useIsApplicationModeInfinity';
import { useTrackingEvent } from 'src/services/useTrackingEvent';
import {
  useBrandVoicesActions,
  useBrandVoicesAssets,
  useCustomerCanCreateFeatureItem,
  useNotificationActions,
  useProjectActions,
} from 'src/store';
import { useBrandVoicePopup } from 'src/views/BrandVoicePopup';
import { useToneOfVoicePopup } from 'src/views/ToneOfVoicePopup';
import { useUpgradePopup } from 'src/views/UpgradePopup';

/**
 * Prepare store/service related props for `ToneOfVoiceList` component
 *
 * @param {GENERATION_TOOL} generationTool - generation tool
 * @param {LANGUAGE} language - language
 * @param {string} eventsPage - "page" attribute for events
 * @param {string} eventsSubPage - "subPage" attribute for events
 * @param {Function} onSelect - "on select" callback
 * @returns {{customModels: [], toneOfVoices: [], isInfinityMode: boolean, onSelect: function, onAddToneOfVoice: function: Promise}}
 */
export const usePrepareToneOfVoiceListProps = ({
  generationTool,
  language,
  eventsPage,
  eventsSubPage,
  onSelect,
}) => {
  const { customModels, toneOfVoices } = useBrandVoicesAssets();
  const isInfinityMode = useIsApplicationModeInfinity();
  const customerCanCreateToneOfVoice = useCustomerCanCreateFeatureItem(
    FEATURE_LIMITS.tonesOfVoicePerCustomer
  );

  const notificationActions = useNotificationActions();
  const projectActions = useProjectActions();
  const handleError = useErrorHandler();
  const openUpgradePopup = useUpgradePopup();
  const openToneOfVoicePopup = useToneOfVoicePopup();
  const openBrandVoicePage = useOpenBrandVoicePage();
  const createCustomModel = useCreateCustomModel(generationTool);
  const getBrandVoiceForToneOfVoice = useGetBrandVoiceForToneOfVoice({ language });
  const trackClickedCreateToneOfVoice = useTrackClickedCreateToneOfVoice({
    generationTool,
    eventsPage,
    eventsSubPage,
  });

  const channel = getToolChannel(generationTool);

  const onAddToneOfVoice = useCallback(
    async ({
      shouldActivateManualStep,
      shouldActiveDraftMode,
      shouldActivateLastStep,
      shouldOnlyTrain,
      draftToneOfVoice,
    }) => {
      // If we are currently on the "multi-prompt-builder" page, we should navigate to the "tone of voice" page
      if (location.pathname.startsWith(`${ROUTES.MULTI_PROMPT_BUILDER}`)) {
        openBrandVoicePage();
        return;
      }

      if (!customerCanCreateToneOfVoice && (shouldActivateLastStep || shouldOnlyTrain)) {
        openUpgradePopup(FEATURE_LIMITS.tonesOfVoicePerCustomer);
        return;
      }

      const brandVoice = getBrandVoiceForToneOfVoice({ shouldActivateLastStep });
      if (!brandVoice) {
        return;
      }

      trackClickedCreateToneOfVoice();

      try {
        const result = await openToneOfVoicePopup({
          brandVoice,
          toneOfVoice: draftToneOfVoice,
          language: getLanguageWithoutFormality(language),
          onCreateCustomModel: channel ? createCustomModel : null,
          page: eventsPage,
          shouldActivateManualStep,
          shouldActivateLastStep,
          shouldOnlyTrain,
          shouldActiveDraftMode,
        });

        if (result?.toneOfVoice) {
          notificationActions.displaySuccess(`Tone of voice '${result.toneOfVoice.name}' created`);

          projectActions.clearDraftToneOfVoice();
          projectActions.updateToneOfVoice(result.toneOfVoice.id);
          onSelect?.({ tov: result.toneOfVoice.id });
        } else if (result?.draftToneOfVoice) {
          projectActions.setDraftToneOfVoice(result.draftToneOfVoice);
          onSelect?.({ draftToneOfVoice: result.draftToneOfVoice });
        }
      } catch (error) {
        handleError(error);
      }
    },
    [
      channel,
      createCustomModel,
      customerCanCreateToneOfVoice,
      eventsPage,
      getBrandVoiceForToneOfVoice,
      handleError,
      language,
      notificationActions,
      onSelect,
      openBrandVoicePage,
      openToneOfVoicePopup,
      openUpgradePopup,
      projectActions,
      trackClickedCreateToneOfVoice,
    ]
  );

  // There are several ways how `onSelect` can be passed into `ToneOfVoiceList`, so we have this fallback in case it's missing
  const onSelectFallback = useCallback(() => {
    log.warning('ToneOfVoiceList: onSelect handler is missing in ToneOfVoiceListProps');
  }, []);

  return {
    customModels,
    toneOfVoices,
    isInfinityMode,
    onSelect: onSelect || onSelectFallback,
    onAddToneOfVoice,
  };
};

const useOpenBrandVoicePage = () => {
  const brandVoicesActions = useBrandVoicesActions();

  return useCallback(() => {
    const brandVoices = brandVoicesActions.getBrandVoices();
    const defaultBrandVoice = brandVoicesActions.getDefaultBrandVoice();

    const brandVoice = defaultBrandVoice || brandVoices[0];
    if (!brandVoice) {
      return null;
    }

    const url = formatRoute(formatBrandVoiceChildRoute(brandVoice.id, ROUTES.TONE_OF_VOICE), {
      [ROUTE_PARAMS.createNew]: true,
    });
    window.open(url, '_blank');
  }, [brandVoicesActions]);
};

const useGetBrandVoiceForToneOfVoice = ({ language }) => {
  const brandVoicesActions = useBrandVoicesActions();
  const openConfirmPopup = useConfirmPopup();
  const openBrandVoicePopup = useBrandVoicePopup();

  return useCallback(
    async ({ shouldActivateLastStep }) => {
      const brandVoices = brandVoicesActions.getBrandVoices();
      const defaultBrandVoice = brandVoicesActions.getDefaultBrandVoice();

      const brandVoiceSameLanguage = isSameLanguage(defaultBrandVoice.language, language)
        ? defaultBrandVoice
        : brandVoices.find((brandVoice) => isSameLanguage(brandVoice.language, language));
      if (brandVoiceSameLanguage) {
        return brandVoiceSameLanguage;
      }

      const brandVoiceNoLanguage = !defaultBrandVoice.language
        ? defaultBrandVoice
        : brandVoices.find((brandVoice) => !brandVoice.language);
      if (brandVoiceNoLanguage) {
        return brandVoiceNoLanguage;
      }

      if (shouldActivateLastStep) {
        const confirmed = await openConfirmPopup({
          mode: CONFIRM_POPUP_MODE.confirmation,
          title: "Tone of voice language doesn't match brand voice",
          content:
            'To save this tone of voice, create a brand voice in the same language as the target audience.',
          buttons: {
            confirm: 'Create new brand voice',
            cancel: 'Close',
          },
        });
        if (confirmed) {
          return await openBrandVoicePopup({
            noRedirect: true,
            language,
            lockLanguage: true,
          });
        }
      }

      return null;
    },
    [brandVoicesActions, language, openBrandVoicePopup, openConfirmPopup]
  );
};

const useTrackClickedCreateToneOfVoice = ({ generationTool, eventsPage, eventsSubPage }) => {
  const trackingEvent = useTrackingEvent();
  const projectActions = useProjectActions();

  return useCallback(() => {
    trackingEvent(events.DOCUMENT.clickedCreateNewToneOfVoice, {
      projectId: projectActions.getCurrentProject()?.id,
      generationTool,
      page: eventsPage,
      subPage: eventsSubPage,
    });
  }, [trackingEvent, projectActions, generationTool, eventsPage, eventsSubPage]);
};
